// Hero-carrusel de la portada: tarjetas grandes (ayer · mes · ocupación · ADR)
// que rotan solas, se deslizan con el dedo y — al tocarlas — SE DAN LA VUELTA
// (flip 3D) para enseñar el detalle por PMS en el reverso. Pedido por Patricio.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import type { Brief, HotelRow, Source } from '@/api/client';
import { C, F, R } from '@/constants/theme';
import { useRole } from '@/context/RoleContext';
import { fmt, fmtDec, fmtE, fmtM } from '@/lib/format';
import { Chip, type ChipKind } from './ui';
import { LiveWaves } from './Waves';

const AUTO_MS = 5500;
const PMS_LBL: Record<string, string> = { noray: 'Noray', onasystem: 'OnaSystem', navision: 'Navision', avalon: 'Avalon' };

function CountUp({ to, style }: { to: number; style: object }) {
  const [v, setV] = useState(0);
  const raf = useRef<number>(0);
  useEffect(() => {
    const t0 = Date.now();
    const dur = 1300;
    const step = () => {
      const p = Math.min(1, (Date.now() - t0) / dur);
      setV(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [to]);
  return <Text style={style}>{fmt(v)}</Text>;
}

/* Tarjeta con dos caras: rotateY 0→180 con backfaceVisibility. */
function FlipCard({ w, front, back, flipped, onToggle }: {
  w: number; front: React.ReactNode; back: React.ReactNode; flipped: boolean; onToggle: () => void;
}) {
  const a = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.spring(a, { toValue: flipped ? 1 : 0, friction: 9, tension: 60, useNativeDriver: true }).start();
  }, [flipped, a]);
  const rf = a.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '180deg'] });
  const rb = a.interpolate({ inputRange: [0, 1], outputRange: ['180deg', '360deg'] });
  return (
    <Pressable style={{ width: w }} onPress={onToggle}>
      <Animated.View style={[s.face, { transform: [{ perspective: 1000 }, { rotateY: rf }] }]}>
        {front}
      </Animated.View>
      <Animated.View style={[s.face, s.backFace, { transform: [{ perspective: 1000 }, { rotateY: rb }] }]}>
        {back}
      </Animated.View>
    </Pressable>
  );
}

interface CardDef {
  key: string;
  lbl: string;
  big: React.ReactNode;
  chips: Array<{ t: string; k: ChipKind }>;
  backTitle: string;
  backRows: Array<[string, string]>;
}

export default function HeroCarousel({ brief, hoteles = [] }: {
  brief: Brief | null; hoteles?: HotelRow[]; source?: Source;
}) {
  const { puede } = useRole();
  const [w, setW] = useState(0);
  const [page, setPage] = useState(0);
  const [flipped, setFlipped] = useState<string | null>(null);
  const scroll = useRef<ScrollView>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const pageRef = useRef(0);
  const flippedRef = useRef<string | null>(null);
  flippedRef.current = flipped;

  const ddmm = (iso?: string) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '');
  const lblPms = (p: string) => PMS_LBL[p] ?? p;

  const cards = useMemo<CardDef[]>(() => {
    const out: CardDef[] = [];
    const fuentes = brief?.fuentes ?? [];
    /* Sin permiso de producción no se pintan estas tarjetas: enseñar «0 €» se
       leería como «el grupo no facturó nada», que es peor que no enseñar nada. */
    if (puede('PRODUCCION') && brief?.prodDia != null) {
      out.push({
        key: 'ayer',
        lbl: `PRODUCCIÓN DE AYER${brief?.fecha ? ` (${ddmm(brief.fecha)})` : ''} · NETO SIN IVA`,
        big: (
          <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 6 }}>
            <CountUp to={brief?.prodDia ?? 0} style={s.big} />
            <Text style={s.euro}>€</Text>
          </View>
        ),
        chips: [
          ...(brief?.deltaDia != null
            ? [{ t: `${brief.deltaDia >= 0 ? '▲ +' : '▼ '}${fmtDec(brief.deltaDia)}% vs budget`, k: (brief.deltaDia >= 0 ? 'good' : 'bad') as ChipKind }]
            : []),
          { t: `${brief?.pms ?? 4} PMS · ${brief?.hoteles ?? 16} hoteles`, k: 'mut' },
        ],
        /* 🔴 AL GIRAR, EL TOP 5 DE HOTELES — no el desglose por PMS. Acuerdo de la
           demo: «facturado ayer por todos los hoteles · al girar, top 5». Y tiene
           sentido: quién más facturó ayer es una pregunta de negocio; el reparto
           por sistema informático es una pregunta de Sistemas, y ya está en el
           chip de la cara delantera («4 PMS · 53 hoteles»).
           Se ordena por `d`, la producción del día de referencia, y se DEJAN FUERA
           los null: un hotel cuyo cargo aún no ha llegado al espejo no es un hotel
           que vendió poco, y colarlo en un top lo diría. */
        backTitle: `Ayer, los que más${brief?.fecha ? ` (${ddmm(brief.fecha)})` : ''}`,
        backRows: [...hoteles]
          .filter((h) => h.d != null && h.d !== 0)
          .sort((a, b) => (b.d ?? 0) - (a.d ?? 0))
          .slice(0, 5)
          .map((h, i) => [`${i + 1}. ${h.n.replace(/^Ona /i, '')}`, fmtE(h.d ?? 0)] as [string, string]),
      });
      out.push({
        key: 'mes',
        lbl: 'PRODUCCIÓN DEL MES · NETO SIN IVA',
        big: <Text style={s.big}>{brief ? fmtM(brief.prodMes) : '—'}</Text>,
        /* 🔴 DICE QUE SON TODOS LOS HOTELES, y no es un adorno. En esta misma
           portada convive el mes del grupo CONTRA PRESUPUESTO, que son solo los
           39 de Control de Gestión (los únicos con budget 2026). Dos cifras del
           mes con distinto universo, una debajo de la otra: si no se dice cuál es
           cuál, la primera pregunta va a ser por qué no cuadran. */
        chips: [
          ...(hoteles.length ? [{ t: `todos los hoteles · ${hoteles.length}`, k: 'mut' as ChipKind }] : []),
          brief?.budPct != null
            ? { t: `${brief.budPct >= 100 ? '▲' : '▼'} ${fmtDec(brief.budPct - 100)}% vs budget`, k: (brief.budPct >= 100 ? 'good' : 'bad') as ChipKind }
            : { t: 'vs budget: próximamente', k: 'mut' },
          ...(brief?.fecha ? [{ t: `hasta el ${ddmm(brief.fecha)}`, k: 'mut' as ChipKind }] : []),
        ],
        backTitle: 'El mes, por PMS',
        backRows: fuentes.map((f) => [lblPms(f.pms), f.mes != null ? fmtM(f.mes) : '—']),
      });
    }
    if (brief?.occ != null) {
      out.push({
        key: 'occ',
        lbl: 'OCUPACIÓN DE HOY · 4 PMS',
        big: <Text style={s.big}>{fmtDec(brief.occ)}<Text style={s.euro}>%</Text></Text>,
        chips: [
          { t: `${brief.hoteles} hoteles con dato`, k: 'mut' },
          { t: 'cada PMS con su doctrina', k: 'mut' },
        ],
        backTitle: 'Ocupación hoy, por PMS',
        backRows: (brief.occPorPms ?? []).map((o) => [`${o.pms} · ${o.hoteles} hot.`, `${fmtDec(o.media)}%`]),
      });
    }
    if (brief?.adr != null) {
      out.push({
        key: 'adr',
        lbl: 'ADR DEL MES',
        big: <Text style={s.big}>{fmtDec(brief.adr)}<Text style={s.euro}> €</Text></Text>,
        chips: [
          { t: 'alojamiento ÷ noches', k: 'mut' },
          { t: 'neto sin IVA', k: 'mut' },
        ],
        backTitle: 'ADR del mes, por PMS',
        backRows: (brief.adrPorPms ?? []).map((a) => [`${lblPms(a.pms)} · ${fmt(a.rn)} noches`, `${fmtDec(a.adr)} €`]),
      });
    }
    if (!out.length) {
      out.push({
        key: 'vacio',
        lbl: 'TU PORTADA',
        big: <Text style={[s.big, { fontSize: 22 }]}>Sin indicadores</Text>,
        chips: [{ t: 'tu perfil de Ona Pulse no incluye estos datos', k: 'mut' }],
        backTitle: '', backRows: [],
      });
    }
    return out;
  }, [brief, puede, hoteles]);

  const go = (i: number, animated = true) => {
    if (!w) return;
    scroll.current?.scrollTo({ x: i * w, animated });
  };

  const arm = () => {
    if (timer.current) clearInterval(timer.current);
    timer.current = setInterval(() => {
      if (flippedRef.current) return;   // con una tarjeta girada, el carrusel espera
      const next = (pageRef.current + 1) % Math.max(cards.length, 1);
      pageRef.current = next;
      setPage(next);
      go(next);
    }, AUTO_MS);
  };

  useEffect(() => {
    arm();
    return () => { if (timer.current) clearInterval(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [w, cards.length]);

  return (
    <View style={s.hero} onLayout={(e) => setW(e.nativeEvent.layout.width)}>
      {w > 0 ? (
        <ScrollView
          ref={scroll}
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          onScrollBeginDrag={() => { if (timer.current) clearInterval(timer.current); }}
          onMomentumScrollEnd={(e) => {
            const p = Math.round(e.nativeEvent.contentOffset.x / w);
            pageRef.current = p;
            setPage(p);
            setFlipped(null);   // al cambiar de tarjeta, se endereza
            arm();
          }}
        >
          {cards.map((c2) => (
            <FlipCard
              key={c2.key}
              w={w}
              flipped={flipped === c2.key}
              onToggle={() => setFlipped(flipped === c2.key ? null : c2.key)}
              front={
                <View style={s.card}>
                  <Text style={s.lbl}>{c2.lbl}</Text>
                  {c2.big}
                  <View style={s.chips}>
                    {c2.chips.map((ch, i) => <Chip key={i} kind={ch.k}>{ch.t}</Chip>)}
                  </View>
                  <Text style={s.hint}>toca para el detalle</Text>
                </View>
              }
              back={
                <View style={s.card}>
                  <Text style={s.lbl}>{c2.backTitle.toUpperCase()}</Text>
                  <View style={{ marginTop: 8, gap: 6 }}>
                    {c2.backRows.length ? c2.backRows.map(([l, v], i) => (
                      <View key={i} style={s.backRow}>
                        <Text style={s.backLbl}>{l}</Text>
                        <Text style={s.backVal}>{v}</Text>
                      </View>
                    )) : <Text style={s.backLbl}>Detalle disponible con el servidor v0.2.2</Text>}
                  </View>
                  <Text style={s.hint}>toca para volver</Text>
                </View>
              }
            />
          ))}
        </ScrollView>
      ) : null}
      <View style={s.dots}>
        {cards.map((c2, i) => (
          <View key={c2.key} style={[s.dot, i === page && s.dotOn]} />
        ))}
      </View>
      <LiveWaves height={72} />
    </View>
  );
}

const s = StyleSheet.create({
  hero: { backgroundColor: '#0C2B3F', borderColor: C.line, borderWidth: 1, borderRadius: R.card, overflow: 'hidden', paddingTop: 16 },
  face: { backfaceVisibility: 'hidden' },
  backFace: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 },
  /* 🔴 156 y no 148: el reverso de «ayer» pasó de 4 filas (los PMS) a 5 (el top
     de hoteles), y el reverso va en `position: absolute` sobre la caja de la cara
     delantera. Con 148 la quinta fila quedaba pegada al pie. Patricio ya lo había
     previsto en el acuerdo: «top 5, solo si entran sin deformar la tarjeta». */
  card: { paddingHorizontal: 18, minHeight: 156, justifyContent: 'flex-start' },
  lbl: { fontFamily: F.uiSemi, fontSize: 11, letterSpacing: 1.6, color: C.aquaSoft, opacity: 0.85 },
  big: { fontFamily: F.display, fontSize: 44, color: '#fff', marginTop: 6, fontVariant: ['tabular-nums'] },
  euro: { fontFamily: F.displayMed, fontSize: 24, color: C.aquaSoft },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 8 },
  hint: { fontFamily: F.ui, fontSize: 9.5, color: C.faint, marginTop: 8 },
  backRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 },
  backLbl: { fontFamily: F.ui, fontSize: 12.5, color: C.muted },
  backVal: { fontFamily: F.uiBold, fontSize: 14, color: C.ink, fontVariant: ['tabular-nums'] },
  dots: { flexDirection: 'row', justifyContent: 'center', gap: 6, paddingTop: 10 },
  dot: { width: 6, height: 6, borderRadius: 3, backgroundColor: 'rgba(140,200,215,0.28)' },
  dotOn: { backgroundColor: C.aqua, width: 16 },
});
